
import React, { useState, useEffect } from "react";
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent, 
  CardFooter 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RecipeIdea } from "../../types";
import useRecipeFormState from "../../hooks/useRecipeFormState";

// Import component parts
import BasicInfo from "./BasicInfo";
import IngredientsList from "./IngredientsList";
import InstructionsField from "./InstructionsField";
import CookingDetails from "./CookingDetails";
import ImageUrlField from "./ImageUrlField";
import TagsList from "./TagsList";

interface RecipeFormProps {
  recipe: RecipeIdea | null;
  onSave: (recipeData: Omit<RecipeIdea, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onCancel: () => void;
}

const RecipeForm: React.FC<RecipeFormProps> = ({ recipe, onSave, onCancel }) => {
  const {
    title,
    setTitle,
    description,
    setDescription,
    ingredients,
    updateIngredient,
    removeIngredient,
    addIngredient,
    instructions,
    setInstructions,
    prepTime,
    setPrepTime,
    cookTime,
    setCookTime,
    totalTime,
    setTotalTime,
    servings,
    setServings,
    difficulty,
    handleDifficultyChange,
    category,
    handleCategoryChange,
    tags,
    updateTag,
    removeTag,
    addTag,
    imageUrl,
    setImageUrl,
    kidFriendly,
    setKidFriendly
  } = useRecipeFormState(recipe);
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Filter out empty ingredients and tags
    const filteredIngredients = ingredients.filter(item => item.trim() !== "");
    const filteredTags = tags.filter(tag => tag.trim() !== "");
    
    onSave({
      title,
      description,
      ingredients: filteredIngredients.length > 0 ? filteredIngredients : [],
      instructions,
      prepTime,
      cookTime,
      totalTime,
      servings,
      difficulty,
      category,
      tags: filteredTags,
      imageUrl: imageUrl || undefined,
      kidFriendly,
    });
  };
  
  return (
    <form onSubmit={handleSubmit}>
      <Card>
        <CardHeader>
          <CardTitle>{recipe ? "Edit Recipe" : "Add New Recipe"}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <BasicInfo
            title={title}
            setTitle={setTitle}
            description={description}
            setDescription={setDescription}
            category={category}
            setCategory={handleCategoryChange}
          />
          
          <IngredientsList
            ingredients={ingredients}
            updateIngredient={updateIngredient}
            removeIngredient={removeIngredient}
            addIngredient={addIngredient}
          />
          
          <InstructionsField
            instructions={instructions}
            setInstructions={setInstructions}
          />
          
          <CookingDetails
            prepTime={prepTime}
            setPrepTime={setPrepTime}
            cookTime={cookTime}
            setCookTime={setCookTime}
            totalTime={totalTime}
            setTotalTime={setTotalTime}
            servings={servings}
            setServings={setServings}
            difficulty={difficulty}
            setDifficulty={handleDifficultyChange}
            kidFriendly={kidFriendly}
            setKidFriendly={setKidFriendly}
          />
          
          <ImageUrlField
            imageUrl={imageUrl}
            setImageUrl={setImageUrl}
          />
          
          <TagsList
            tags={tags}
            updateTag={updateTag}
            removeTag={removeTag}
            addTag={addTag}
          />
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" className="bg-elfGreen hover:bg-elfGreen-dark">
            {recipe ? "Update Recipe" : "Create Recipe"}
          </Button>
        </CardFooter>
      </Card>
    </form>
  );
};

export default RecipeForm;
